import { useEffect } from 'react';

/**
 * useSeo — declarative SEO + Open Graph + Twitter Card meta injection.
 *
 * Sets / updates the standard meta tags expected by Google, Facebook, LinkedIn,
 * WhatsApp, Twitter/X, iMessage etc. when the slug component mounts. Cleans up
 * by resetting to the site defaults on unmount.
 *
 * Notes
 * - Uses Open Graph + Twitter "summary_large_image" card variant
 * - Absolute URLs are required for OG images — pass `image` as a full URL
 * - JSON-LD breadcrumb / Project schema can be added later if needed
 */
const SITE = {
  name: 'Guardian Artificial Lawns',
  url: 'https://guardianartificiallawns.co.uk',
  defaultTitle: 'Guardian Artificial Lawns — London, Kent, Surrey & Essex',
  defaultDescription:
    'Premium artificial lawn installation across London, Kent, Surrey & Essex. Civil-engineered foundations, 10-year guarantee. Free site survey & 48-hour quote.',
  defaultImage: 'https://guardianartificiallawns.co.uk/og-image.jpg',
  twitter: '@guardianlawns',
  locale: 'en_GB',
};

const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

function formatTitle(title) {
  if (!title) return SITE.defaultTitle;
  if (title.includes(SITE.name)) return title;
  return `${title} · ${SITE.name}`;
}

function absoluteUrl(path, fallback) {
  if (!path) return fallback;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE.url}${path.startsWith('/') ? path : `/${path}`}`;
}

function setMeta(attr, key, value) {
  if (!value) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  return el;
}

function setLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

export default function useSeo({
  title,
  description,
  image,
  url,
  type = 'article',
  jsonLd = null,
  robots = DEFAULT_ROBOTS,
}) {
  useEffect(() => {
    const fullTitle = formatTitle(title);
    const desc = description || SITE.defaultDescription;
    const img = absoluteUrl(image, SITE.defaultImage);
    // Always anchor canonical to the live brand domain — never the
    // preview/staging host the page is currently being viewed from.
    // If a page passes an explicit `url`, use that.  Otherwise
    // construct it from the current pathname + the SITE base URL.
    let canonical;
    if (url) {
      canonical = url;
    } else if (typeof window !== 'undefined') {
      const pathAndQuery = window.location.pathname + window.location.search;
      canonical = `${SITE.url}${pathAndQuery}`.replace(/\/$/, '') || SITE.url;
      // Preserve the trailing slash for the home route only.
      if (window.location.pathname === '/') canonical = `${SITE.url}/`;
    } else {
      canonical = SITE.url;
    }

    const prevTitle = document.title;
    document.title = fullTitle;

    setMeta('name', 'description', desc);
    setMeta('name', 'robots', robots);
    setMeta('name', 'googlebot', robots);
    setLink('canonical', canonical);

    // Open Graph
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', SITE.name);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', img);
    setMeta('property', 'og:image:alt', title || SITE.name);
    setMeta('property', 'og:locale', SITE.locale);

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', SITE.twitter);
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', img);

    // JSON-LD structured data — supports an object or an array of objects.
    // Each entry is injected as its own <script type="application/ld+json">
    // tag and removed on unmount so per-page schema can come and go cleanly.
    const ldNodes = [];
    if (jsonLd) {
      const ldList = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      ldList.forEach((data) => {
        const node = document.createElement('script');
        node.type = 'application/ld+json';
        node.setAttribute('data-source', 'useSeo');
        node.text = JSON.stringify(data);
        document.head.appendChild(node);
        ldNodes.push(node);
      });
    }

    return () => {
      // Restore to defaults on unmount so other pages can override cleanly
      document.title = prevTitle || SITE.defaultTitle;
      setMeta('name', 'robots', DEFAULT_ROBOTS);
      setMeta('name', 'googlebot', DEFAULT_ROBOTS);
      ldNodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [title, description, image, url, type, jsonLd, robots]);
}
